import React, { useMemo, useState } from 'react';
import type {
  LpPage,
  LpPhaseKey,
  LpStat,
  LpTesting,
  LpTone,
} from '../../growth-landing-pages/types';
import {
  LP_ARCHIVED_COUNT,
  LP_PAGES,
  LP_STATS,
} from '../../growth-landing-pages/mockData';
import {
  GROWTH_LANDING_PAGES_META,
  LP_PHASE_META,
  LP_PHASE_ORDER,
  LP_PRESETS,
  LP_STATUS_META,
} from '../../growth-landing-pages/config';
import { Icon } from '../Icon/Icon';
import { Segmented } from '../Segmented/Segmented';
import './growth-landing-pages.css';

export const LP_TONE: Record<LpTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  lav: { accent: '#7A5CFF', tint: 'rgba(122,79,214,0.13)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function LpCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function LpStatTile({ stat }: { stat: LpStat }) {
  const tone = LP_TONE[stat.tone];
  return (
    <LpCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{stat.label}</div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span
          className="font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em]"
          style={{ color: stat.tone === 'neutral' ? 'var(--ink-0)' : tone.accent }}
        >
          {stat.value}
        </span>
        {stat.suffix ? <span className="text-[14px] text-[var(--ink-4)]">{stat.suffix}</span> : null}
      </div>
      <div className="mt-2 text-[12.5px] text-[var(--ink-3)]">{stat.sub}</div>
    </LpCard>
  );
}

function LpStatusPill({ page }: { page: LpPage }) {
  const status = LP_STATUS_META[page.status] ?? LP_STATUS_META.live;
  const tone = LP_TONE[status.tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-2.5 py-1 text-[11px] font-semibold"
      style={{ background: tone.tint, color: tone.accent }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{
          background: tone.accent,
          boxShadow: page.status === 'live' ? `0 0 6px ${tone.accent}` : undefined,
        }}
      />
      {status.label}
    </span>
  );
}

function LpTestingBadge({ testing }: { testing: LpTesting }) {
  const phase = LP_PHASE_META[testing.phase];
  const tone = LP_TONE[phase.tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--r-sm)] border px-2.5 py-1 text-[11.5px] font-semibold"
      style={{
        background: tone.tint,
        color: tone.accent,
        borderColor: `color-mix(in srgb, ${tone.accent} 30%, transparent)`,
      }}
    >
      <Icon kind="bolt" size={11} stroke={2.2} />
      {phase.label}
      {testing.phase !== 'converged' && testing.activeVariants > 1 ? (
        <span className="opacity-75">· {testing.activeVariants} variants</span>
      ) : null}
      {testing.leader ? <span className="opacity-75">· Variant {testing.leader} leading</span> : null}
    </span>
  );
}

const THUMB_TONE: Record<string, string> = {
  code_red: 'var(--terracotta)',
  high_ticket: '#7A5CFF',
  transactional: 'var(--info)',
  new_local: 'var(--success)',
  offer_funnel: 'var(--gold)',
  anxiety: '#7A5CFF',
  family: 'var(--success)',
};

function LpThumbnail({ page }: { page: LpPage }) {
  const accent = THUMB_TONE[page.preset] ?? 'var(--ink-4)';
  return (
    <div className="hidden h-[120px] w-[170px] shrink-0 flex-col overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] sm:flex">
      <div className="h-2" style={{ background: accent }} />
      <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-3 py-2">
        <div className="h-1.5 w-[60%] rounded-full" style={{ background: 'var(--ink-2)' }} />
        <div className="h-1 w-[42%] rounded-full bg-[var(--ink-4)]" />
        <div className="mt-1.5 h-2.5 w-12 rounded-[3px]" style={{ background: accent }} />
      </div>
    </div>
  );
}

function LpPhaseProgress({ testing }: { testing: LpTesting }) {
  const isConverged = testing.phase === 'converged';
  const currentIdx = LP_PHASE_ORDER.indexOf(testing.phase);
  return (
    <div className="flex items-center gap-1.5">
      {LP_PHASE_ORDER.map((phase, index) => {
        const isPast = isConverged || index < currentIdx;
        const isCurrent = !isConverged && index === currentIdx;
        const width = phase === 'exploration' ? 'w-5' : phase === 'validation' ? 'w-7' : 'w-12';
        return (
          <span
            key={phase}
            className={`h-1.5 rounded-full ${width}`}
            style={{
              background: isPast ? 'var(--success)' : isCurrent ? 'var(--info)' : 'var(--cream-2)',
            }}
          />
        );
      })}
      {isConverged ? (
        <Icon kind="check" size={12} stroke={2.5} className="ml-0.5 text-[var(--success)]" />
      ) : null}
    </div>
  );
}

function LpProgressLabel({ testing }: { testing: LpTesting }) {
  if (testing.phase === 'exploitation') return `Winner running (${testing.leader ?? '—'})`;
  if (testing.phase === 'converged') return `Stable ${String(testing.daysInExploitation ?? 0)}d`;
  return `${String(testing.budgetSpent)}% budget spent`;
}

function LpPageRow({
  page,
  onEdit,
  onViewLive,
  onReviewApprove,
}: {
  page: LpPage;
  onEdit?: (id: string) => void;
  onViewLive?: (id: string, url: string) => void;
  onReviewApprove?: (id: string) => void;
}) {
  const preset = LP_PRESETS[page.preset];
  const presetTone = LP_TONE[preset.tone];
  const isPending = page.status === 'pending_approval';
  const isLive = page.status === 'live';
  const isPaused = page.status === 'paused';
  const showPerf = (isLive || isPaused) && page.performance;

  return (
    <LpCard
      className={[
        'flex gap-5 p-5',
        isPending ? '!border-[color-mix(in_srgb,var(--gold)_45%,transparent)]' : '',
      ].join(' ')}
    >
      <LpThumbnail page={page} />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center rounded-[var(--r-pill)] px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.04em]"
                style={{ background: presetTone.tint, color: presetTone.accent }}
              >
                {preset.label}
              </span>
              <LpStatusPill page={page} />
              {page.testing ? <LpTestingBadge testing={page.testing} /> : null}
            </div>
            <h3 className="m-0 font-[var(--font-display)] text-[18px] font-semibold leading-[1.3] tracking-[-0.015em] text-[var(--ink-0)]">
              {page.title}
            </h3>
            <p className="mt-1 inline-flex items-center gap-1.5 font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">
              <Icon kind="link" size={11} stroke={2} />
              {page.url}
            </p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            {isLive && onViewLive ? (
              <button
                type="button"
                onClick={() => onViewLive(page.id, page.url)}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--ink-2)]"
              >
                <Icon kind="external" size={12} stroke={2} />
                View Live
              </button>
            ) : isLive ? (
              <span className="inline-flex items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--ink-2)]">
                <Icon kind="external" size={12} stroke={2} />
                View Live
              </span>
            ) : null}
            {isPending && onReviewApprove ? (
              <button
                type="button"
                onClick={() => onReviewApprove(page.id)}
                className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] px-3.5 py-1.5 text-[12.5px] font-semibold text-[var(--cream-1)]"
                style={{ background: 'var(--gold)' }}
              >
                Review & Approve
              </button>
            ) : isPending ? (
              <span
                className="inline-flex items-center rounded-[var(--r-sm)] px-3.5 py-1.5 text-[12.5px] font-semibold text-[var(--cream-1)]"
                style={{ background: 'var(--gold)' }}
              >
                Review & Approve
              </span>
            ) : onEdit ? (
              <button
                type="button"
                onClick={() => onEdit(page.id)}
                className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] px-3.5 py-1.5 text-[12.5px] font-semibold text-[var(--cream-1)]"
                style={{ background: 'var(--success)' }}
              >
                Edit
              </button>
            ) : (
              <span
                className="inline-flex items-center rounded-[var(--r-sm)] px-3.5 py-1.5 text-[12.5px] font-semibold text-[var(--cream-1)]"
                style={{ background: 'var(--success)' }}
              >
                Edit
              </span>
            )}
          </div>
        </div>

        <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-2.5 py-1.5">
          <Icon kind="grid" size={13} stroke={2} className="text-[var(--ink-3)]" />
          <span className="text-[12px] text-[var(--ink-3)]">Campaign:</span>
          <span className="text-[12.5px] font-semibold text-[var(--ink-1)] underline decoration-[var(--cream-2)] underline-offset-2">
            {page.campaign.name}
          </span>
          {page.campaign.budget > 0 ? (
            <>
              <span className="text-[var(--ink-4)]">·</span>
              <span className="font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">
                ${page.campaign.budget.toLocaleString()}/mo
              </span>
            </>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[12px] text-[var(--ink-3)]">
          <span>{page.blocks} blocks</span>
          <span className="text-[var(--ink-4)]">·</span>
          <span>
            ELVA confidence:{' '}
            <span
              className="font-semibold"
              style={{
                color:
                  page.confidence != null && page.confidence > 90 ? 'var(--success)' : 'var(--gold)',
              }}
            >
              {page.confidence != null ? `${page.confidence}%` : '—'}
            </span>
          </span>
          <span className="text-[var(--ink-4)]">·</span>
          <span>Created {page.created}</span>
        </div>

        {showPerf && page.performance ? (
          <div className="lp-perf-strip mt-3.5 flex flex-wrap items-center gap-x-0 gap-y-3 border-t border-[var(--cream-2)] pt-3">
            <div className="min-w-[140px] flex-1 pr-3.5">
              <div className={`${eyebrowClass} mb-1`}>Conversions</div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                  {page.performance.conversions}
                </span>
                <span className="text-[11px] text-[var(--ink-3)]">
                  ({page.performance.formFills} forms · {page.performance.calls} calls)
                </span>
              </div>
            </div>
            <div className="min-w-[110px] flex-1 px-3.5">
              <div className={`${eyebrowClass} mb-1`}>Conversion Rate</div>
              <div
                className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em]"
                style={{ color: page.performance.conversionRate > 5 ? 'var(--success)' : 'var(--ink-0)' }}
              >
                {page.performance.conversionRate}%
              </div>
            </div>
            <div className="min-w-[90px] flex-1 px-3.5">
              <div className={`${eyebrowClass} mb-1`}>Visits</div>
              <div className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                {page.performance.visits.toLocaleString()}
              </div>
            </div>
            {page.testing && !isPaused ? (
              <div className="min-w-[180px] flex-[1.4] pl-3.5">
                <div className={`${eyebrowClass} mb-1.5`}>Optimization Progress</div>
                <div className="flex items-center gap-2.5">
                  <LpPhaseProgress testing={page.testing} />
                  <span className="text-[11.5px] font-medium text-[var(--ink-2)]">
                    <LpProgressLabel testing={page.testing} />
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {isPending ? (
          <div className="mt-3 flex items-start gap-2 rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--gold)_30%,transparent)] bg-[var(--gold-tint)] px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
            <Icon kind="info" size={14} stroke={2} className="mt-0.5 shrink-0 text-[var(--gold)]" />
            ELVA composed this page and is waiting for your approval. Once live, it will automatically test 4
            variants to find the highest-converting version.
          </div>
        ) : null}

        {isPaused && page.pausedReason ? (
          <div className="mt-2.5 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3 py-2 text-[11.5px] italic text-[var(--ink-3)]">
            {page.pausedReason}
          </div>
        ) : null}
      </div>
    </LpCard>
  );
}

function LpGroup({
  title,
  tone,
  pages,
  onEdit,
  onViewLive,
  onReviewApprove,
}: {
  title: string;
  tone: LpTone;
  pages: LpPage[];
  onEdit?: (id: string) => void;
  onViewLive?: (id: string, url: string) => void;
  onReviewApprove?: (id: string) => void;
}) {
  if (pages.length === 0) return null;
  const t = LP_TONE[tone];
  return (
    <div className="mb-8">
      <div className="mb-3.5 flex items-center gap-2.5">
        <h2 className="m-0 font-[var(--font-display)] text-[18px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          {title}
        </h2>
        <span
          className="rounded-[var(--r-pill)] px-2.5 py-0.5 text-[12px] font-semibold"
          style={{ background: t.tint, color: t.accent }}
        >
          {pages.length}
        </span>
      </div>
      <div className="space-y-3">
        {pages.map((page) => (
          <LpPageRow
            key={page.id}
            page={page}
            {...(onEdit ? { onEdit } : {})}
            {...(onViewLive ? { onViewLive } : {})}
            {...(onReviewApprove ? { onReviewApprove } : {})}
          />
        ))}
      </div>
    </div>
  );
}

function LpOptimizationLegend() {
  return (
    <LpCard className="mb-7 flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5">
      <div className={`${eyebrowClass} shrink-0`}>How ELVA optimizes:</div>
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
        {LP_PHASE_ORDER.map((phaseKey, index) => {
          const phase = LP_PHASE_META[phaseKey];
          const tone = LP_TONE[phase.tone];
          const desc =
            phaseKey === 'exploration'
              ? 'Test 3-4 variants'
              : phaseKey === 'validation'
                ? 'Top 2 face off'
                : 'Winner takes all';
          return (
            <div key={phaseKey} className="flex items-center gap-3.5">
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border font-[var(--font-mono)] text-[10px] font-bold"
                  style={{
                    background: tone.tint,
                    color: tone.accent,
                    borderColor: `color-mix(in srgb, ${tone.accent} 30%, transparent)`,
                  }}
                >
                  {index + 1}
                </span>
                <div className="text-[12.5px]">
                  <span className="font-semibold text-[var(--ink-1)]">{phase.label}</span>
                  <span className="ml-1.5 text-[var(--ink-3)]">
                    ({phase.pct}% budget · {desc})
                  </span>
                </div>
              </div>
              {index < LP_PHASE_ORDER.length - 1 ? (
                <Icon kind="right" size={12} stroke={2.5} className="text-[var(--ink-4)]" />
              ) : null}
            </div>
          );
        })}
      </div>
    </LpCard>
  );
}

export interface LandingPagesViewProps {
  items?: LpPage[];
  stats?: LpStat[];
  archivedCount?: number;
  loading?: boolean;
  onEdit?: (id: string) => void;
  onViewLive?: (id: string, url: string) => void;
  onReviewApprove?: (id: string) => void;
  onNewLandingPage?: () => void;
}

export function LandingPagesView({
  items: itemsProp,
  stats: statsProp,
  archivedCount: archivedCountProp,
  loading = false,
  onEdit,
  onViewLive,
  onReviewApprove,
  onNewLandingPage,
}: LandingPagesViewProps = {}) {
  const meta = GROWTH_LANDING_PAGES_META;
  const controlled = itemsProp !== undefined;
  const sourcePages = controlled ? itemsProp : LP_PAGES;
  const stats = statsProp ?? LP_STATS;
  const archivedCount = archivedCountProp ?? LP_ARCHIVED_COUNT;

  const campaignOptions = useMemo(() => {
    const names = Array.from(new Set(sourcePages.map((page) => page.campaign.name)));
    return ['All campaigns', ...names];
  }, [sourcePages]);
  const [campaignFilter, setCampaignFilter] = useState<string>('All campaigns');

  const visiblePages = useMemo(
    () =>
      campaignFilter === 'All campaigns'
        ? sourcePages
        : sourcePages.filter((page) => page.campaign.name === campaignFilter),
    [campaignFilter, sourcePages],
  );

  const pendingPages = visiblePages.filter((page) => page.status === 'pending_approval');
  const livePages = visiblePages.filter((page) => page.status === 'live');
  const pausedPages = visiblePages.filter((page) => page.status === 'paused');

  return (
    <div className="growth-landing-pages-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-[680px]">
            <div className="mb-2 flex items-center gap-2">
              <Icon kind="grid" size={13} stroke={2} className="text-[var(--ai-3)]" />
              <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
                {meta.eyebrow}
              </span>
            </div>
            <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
              {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
            </h1>
            <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
              {meta.description}
            </p>
          </div>
          {onNewLandingPage ? (
            <button
              type="button"
              onClick={onNewLandingPage}
              className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-4 text-sm font-semibold text-[var(--cream-1)] shadow-[var(--sh-1)]"
              style={{ background: 'var(--success)' }}
            >
              <Icon kind="plus" size={16} stroke={2.4} />
              New Landing Page
            </button>
          ) : null}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2.5">
          <span className="text-[12px] font-medium text-[var(--ink-3)]">Filter:</span>
          <Segmented options={campaignOptions} value={campaignFilter} onChange={setCampaignFilter} />
        </div>
      </div>

      <div
        className="lp-kpi-grid mb-7 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {stats.map((stat) => (
          <LpStatTile key={stat.key} stat={stat} />
        ))}
      </div>

      <LpOptimizationLegend />

      <LpGroup
        title="Pending Your Approval"
        tone="gold"
        pages={pendingPages}
        {...(onEdit ? { onEdit } : {})}
        {...(onViewLive ? { onViewLive } : {})}
        {...(onReviewApprove ? { onReviewApprove } : {})}
      />
      <LpGroup
        title="Live"
        tone="success"
        pages={livePages}
        {...(onEdit ? { onEdit } : {})}
        {...(onViewLive ? { onViewLive } : {})}
        {...(onReviewApprove ? { onReviewApprove } : {})}
      />
      <LpGroup
        title="Paused"
        tone="neutral"
        pages={pausedPages}
        {...(onEdit ? { onEdit } : {})}
        {...(onViewLive ? { onViewLive } : {})}
        {...(onReviewApprove ? { onReviewApprove } : {})}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--r-xl)] border border-dashed border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-4">
        <div className="text-[13px] text-[var(--ink-2)]">
          <span className="font-semibold text-[var(--ink-1)]">{archivedCount} archived pages</span> from past
          campaigns
        </div>
        <span className="inline-flex items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-1.5 text-[12.5px] font-medium text-[var(--ink-2)]">
          Show archived
        </span>
      </div>
    </div>
  );
}
